import { api } from './client';
import type { AgencyBrand } from '../types';
import type { NewTraveller } from './trips.api';

export interface AgencyDetail extends AgencyBrand {
  /** ADR-0034: the agency's ceilings, Maps in milli-euro. */
  mapsCapUnits: number;
  aiMonthlyRequests: number;
  usage: { mapsUnits: number; aiRequests: number };
}

export type AgencyRole = 'ADMIN' | 'AGENT';

/** ADR-0036: an agency the user works at, and what they are in it. */
export interface AgencyMembership extends AgencyBrand {
  role: AgencyRole;
}

export interface TeamMember {
  userId: string;
  name: string;
  email: string | null;
  avatarUrl: string | null;
  role: AgencyRole;
  joinedAt: string;
}

export interface TeamInvitation {
  id: string;
  email: string;
  role: AgencyRole;
  expiresAt: string;
  createdAt: string;
}

export interface MemberInvitationPreview {
  agencyName: string;
  logoUrl: string | null;
  email: string;
  role: AgencyRole;
}

/** ADR-0037: one client of the agency's book. */
export interface AgencyClient {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  birthDate: string | null;
  notes: string | null;
  createdAt: string;
  hasAccount: boolean;
  tripCount: number;
}

export interface AgencyClientDetail extends AgencyClient {
  trips: { id: string; name: string; destination: string; startDate: string; endDate: string; status: TravellerStatus }[];
}

export interface ClientInput {
  name: string;
  email?: string | null;
  phone?: string | null;
  birthDate?: string | null;
  notes?: string | null;
}

export type AgencyTripScope = 'current' | 'past' | 'templates';
export type TravellerStatus = 'NOT_INVITED' | 'INVITED' | 'ACTIVATED' | 'JOINED';

export interface AgencyTraveller {
  userId: string;
  name: string;
  isLead: boolean;
  status: TravellerStatus;
  email: string | null;
  /** While INVITED: what the agency hands its client. */
  code: string | null;
  activatedAt: string | null;
}

export interface AgencyTrip {
  id: string;
  name: string;
  destination: string;
  startDate: string;
  endDate: string;
  nights: number;
  /** Circuits only: the trip's notes, its description. */
  description: string | null;
  coverImageUrl: string | null;
  isTemplate: boolean;
  controlEndsAt: string | null;
  /** False once the agency's control is over: the trip is the client's alone. */
  canOpen: boolean;
  travellers: AgencyTraveller[];
}

export interface Activation {
  tripId: string;
  tripName: string;
  startDate: string;
  endDate: string;
  email: string;
  activatedAt: string;
}

export interface DepartureInput {
  name?: string;
  startDate: string;
  travellers: NewTraveller[];
}

export const agenciesApi = {
  mine: () => api.get<AgencyMembership[]>('/agencies/mine').then(r => r.data),

  get: (agencyId: string) => api.get<AgencyDetail>(`/agencies/${agencyId}`).then(r => r.data),

  update: (agencyId: string, data: { name?: string; emergencyPhone?: string | null; contactEmail?: string | null }) =>
    api.patch<AgencyBrand>(`/agencies/${agencyId}`, data).then(r => r.data),

  uploadLogo: (agencyId: string, file: Blob) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<{ logoUrl: string }>(`/agencies/${agencyId}/logo`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  deleteLogo: (agencyId: string) => api.delete<{ logoUrl: null }>(`/agencies/${agencyId}/logo`).then(r => r.data),

  trips: (agencyId: string, scope: AgencyTripScope) =>
    api.get<AgencyTrip[]>(`/agencies/${agencyId}/trips`, { params: { scope } }).then(r => r.data),

  createDeparture: (agencyId: string, templateId: string, data: DepartureInput) =>
    api.post<{ id: string; invitesFailed: number }>(`/agencies/${agencyId}/templates/${templateId}/departures`, data).then(r => r.data),

  duplicateCircuit: (agencyId: string, templateId: string, name: string) =>
    api.post<{ id: string }>(`/agencies/${agencyId}/templates/${templateId}/duplicate`, { name }).then(r => r.data),

  activations: (agencyId: string, month: string) =>
    api.get<Activation[]>(`/agencies/${agencyId}/activations`, { params: { month } }).then(r => r.data),

  team: (agencyId: string) =>
    api.get<{ members: TeamMember[]; invitations: TeamInvitation[] }>(`/agencies/${agencyId}/team`).then(r => r.data),

  inviteMember: (agencyId: string, email: string, role: AgencyRole, locale: string) =>
    api.post(`/agencies/${agencyId}/team/invitations`, { email, role, locale }),

  revokeMemberInvitation: (agencyId: string, invitationId: string) =>
    api.delete(`/agencies/${agencyId}/team/invitations/${invitationId}`),

  changeMemberRole: (agencyId: string, userId: string, role: AgencyRole) =>
    api.patch(`/agencies/${agencyId}/team/${userId}`, { role }),

  removeMember: (agencyId: string, userId: string) => api.delete(`/agencies/${agencyId}/team/${userId}`),

  previewMemberInvitation: (token: string) =>
    api.get<MemberInvitationPreview>(`/agencies/invitations/${encodeURIComponent(token)}`).then(r => r.data),

  acceptMemberInvitation: (token: string) =>
    api.post<{ agencyId: string }>(`/agencies/invitations/${encodeURIComponent(token)}/accept`).then(r => r.data),

  clients: (agencyId: string, q?: string) =>
    api.get<AgencyClient[]>(`/agencies/${agencyId}/clients`, { params: q ? { q } : {} }).then(r => r.data),

  client: (agencyId: string, clientId: string) =>
    api.get<AgencyClientDetail>(`/agencies/${agencyId}/clients/${clientId}`).then(r => r.data),

  createClient: (agencyId: string, data: ClientInput) =>
    api.post<AgencyClient>(`/agencies/${agencyId}/clients`, data).then(r => r.data),

  updateClient: (agencyId: string, clientId: string, data: Partial<ClientInput>) =>
    api.patch<AgencyClient>(`/agencies/${agencyId}/clients/${clientId}`, data).then(r => r.data),

  deleteClient: (agencyId: string, clientId: string) => api.delete(`/agencies/${agencyId}/clients/${clientId}`),
};
